/* eslint-disable no-nested-ternary */
import { useRequest } from 'features/request/useRequest';
import type {
    ActKc3RegistryItem,
    ActKc3RegistryItemFilterInput,
    ContractObjectStatusEnum,
    WorkOrderStatusEnum,
} from 'gql/graphql';
import { ActKc3LevelEnum } from 'gql/graphql';
import { destroyAgGridTableFilters } from 'utils';

import type { GridApi } from 'ag-grid-community';
import * as R from 'ramda';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { uerContractActsKC3Gql } from '../actKC3ListQueries';
import type { ActListItemWithGroupInfo, GroupCollapseState } from '../types';

interface IUseActKC3ListData {
    contractId: number;
    tableApi: GridApi | undefined;
    withEffect?: boolean;
    actKC3Level?: ActKc3LevelEnum;
    levelItems?: { id: number; name: string; status?: WorkOrderStatusEnum | ContractObjectStatusEnum }[];
}

interface IGetGroupedListData {
    items?: ActKc3RegistryItem[] | null;
    levelItems?: {
        hasWorkOrdersInProgress?: boolean;
        id: number;
        name: string;
        status?: WorkOrderStatusEnum | ContractObjectStatusEnum;
    }[];
    actKC3Level?: ActKc3LevelEnum;
}

const getActKC3Id = (item: ActListItemWithGroupInfo) => item.actKC3Id;

export const groupByActKC3Id = R.groupBy(getActKC3Id);

const getGroupedListData = (props: IGetGroupedListData) => {
    const { items, levelItems, actKC3Level } = props;

    if (!items || !levelItems) {
        return undefined;
    }

    const levelLinkKey =
        actKC3Level === ActKc3LevelEnum.Contract
            ? 'contractId'
            : actKC3Level === ActKc3LevelEnum.WorkOrder
            ? 'workOrderId'
            : 'rootContractObjectId';

    const groupedItemsMap = levelItems.reduce(
        (
            acc: {
                items: Map<number, ActListItemWithGroupInfo[]>;
                initialCollapseState: GroupCollapseState;
            },
            item,
        ) => {
            const itemsForTopLevel = items.filter((i) => i[levelLinkKey] === item.id);
            const itemsForTopLevelGrouped = groupByActKC3Id(itemsForTopLevel);
            const topLevelItem = {
                number: item.name,
                level: 0,
                id: item.id,
                isGroup: true,
                groupKey: 'root',
                groupStatus: item.status,
                childGroups: itemsForTopLevel.length ? [item.id] : [],
                hasWorkOrdersInProgress: item?.hasWorkOrdersInProgress,
            } as unknown as ActListItemWithGroupInfo;

            const subLevelItems = Object.keys(itemsForTopLevelGrouped).reduce((accSub, actKC3IdKey) => {
                const groupItems = itemsForTopLevelGrouped[actKC3IdKey];

                groupItems.forEach((gi, indexGroup) => {
                    const groupKey = `${item.id}_${gi.actKC3Id}`;

                    if (!indexGroup) {
                        accSub.push({
                            ...gi,
                            id: gi.actKC3Id,
                            isGroup: true,
                            level: 1,
                            groupKey: String(item.id),
                            groupStatus: item.status,
                            childGroups: [groupKey],
                            hasWorkOrdersInProgress: item?.hasWorkOrdersInProgress,
                        } as unknown as ActListItemWithGroupInfo);
                    }

                    topLevelItem.childGroups!.push(groupKey);
                    accSub.push({ ...gi, groupKey, id: gi.actKC2Id });
                    acc.initialCollapseState[groupKey] = true;
                });

                return accSub;
            }, [] as ActListItemWithGroupInfo[]);

            acc.items.set(item.id, [topLevelItem, ...subLevelItems]);
            acc.initialCollapseState[item.id] = false;

            return acc;
        },
        {
            items: new Map(),
            initialCollapseState: {},
        },
    );

    const itemsWithGroups = Array.from(groupedItemsMap.items.values()).flat();

    return {
        itemsWithGroups,
        initialCollapseState: groupedItemsMap.initialCollapseState,
    };
};

const filterNames: Record<'number' | 'actKC3Date', string> = {
    number: 'actKC3FullNumber',
    actKC3Date: 'actKC3Date',
};

export const useActKC3ListData = (props: IUseActKC3ListData) => {
    const { contractId, withEffect = true, tableApi, actKC3Level, levelItems } = props;

    const dispatch = useDispatch();

    const [collapseState, setCollapseState] = useState<GroupCollapseState>({});

    const [whereState, setWhereState] = useState<ActKc3RegistryItemFilterInput | undefined>(undefined);

    const changeFilterHandler = useCallback(
        ({ fieldName, checkedValues }: { fieldName: 'number' | 'actKC3Date'; checkedValues: Array<string> }) => {
            setWhereState((p) => ({
                ...p,
                [filterNames[fieldName]]: checkedValues?.length
                    ? {
                          or: checkedValues?.map((number) => ({
                              eq: number,
                          })),
                      }
                    : undefined,
            }));
        },
        [],
    );

    const variables = useMemo(
        () => ({
            contractId,
            where: whereState,
        }),
        [contractId, whereState],
    );

    const [listData, , , request] = useRequest({
        requestId: 'actKC3List',
        query: uerContractActsKC3Gql,
        variables,
        withEffect: Boolean(contractId && withEffect),
    });

    const groupedListData = useMemo(
        () => getGroupedListData({ items: listData?.uerContractActsKC3?.items, actKC3Level, levelItems }),
        [actKC3Level, levelItems, listData?.uerContractActsKC3?.items],
    );

    useEffect(() => {
        if (groupedListData?.initialCollapseState) {
            setCollapseState((prevState) => ({ ...groupedListData?.initialCollapseState, ...prevState }));
        }
    }, [groupedListData?.initialCollapseState]);

    const actualGroupedItems = useMemo(
        () => groupedListData?.itemsWithGroups.filter((item) => item.groupKey && !collapseState[item.groupKey]),
        [collapseState, groupedListData?.itemsWithGroups],
    );

    const changeCollapseState = useCallback(
        (childGroups: string[]) => () => {
            setCollapseState((prevState) => {
                const switchState = childGroups.reduce(
                    (acc, key, _index, arr) => {
                        acc[key] = !prevState[arr[0]];

                        return acc;
                    },
                    {} as Record<string, boolean>,
                );

                return { ...prevState, ...switchState };
            });
        },
        [],
    );

    const itemsCount = useMemo(
        () => Object.keys(groupedListData?.initialCollapseState ?? {}).length,
        [groupedListData?.initialCollapseState],
    );

    const refreshListActListData = useCallback(() => {
        dispatch(request(variables));
        destroyAgGridTableFilters({
            api: tableApi,
            columns: ['number', 'actKC3Date'],
        });
    }, [dispatch, request, tableApi, variables]);

    return {
        items: actualGroupedItems,
        changeCollapseState,
        collapseState,
        itemsCount,
        changeFilterHandler,
        refreshListActListData,
    };
};
